import { useContext, useCallback, useMemo, useState, useEffect } from 'react'
import { ReservationContext } from './context/reservation-context'
import { Box, Divider, Grid, Stack } from '@vapor/material'
import { BillsTab } from './tabs/bills-tab/bills-tab'
import ReservationHeader from './header/header'
import ReservedResourceFilter from './reserved-resource-filter/reserved-resource-filter'
import { ReservedResourcesTab } from './tabs/reserved-resources-tab/reserved-resources-tab'
import { TimeReservedResourcesTab } from './tabs/time-reserved-resource-tab/time-reserved-resources-tab'
import { TabPanel, Tabs } from './tabs/tabs'
import { HiCIcon } from 'src/app/components/icons/icon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { useTranslation } from 'react-i18next'
import { ReservedResourcePhase, useGetReservationEmailsQuery, useReservationDeleteMutation, useReservationThreadsQuery } from 'src/api/api.generated'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useTenant } from 'src/app/hooks/context/context.hook'
import MessagesTab, { ReservationThread } from './tabs/messages-tab/messages-tab'
import AttachesTab from './tabs/attaches-tab/attaches-tab'
import { BillF, DepositF, ReservationF, ReservationHeaderTab } from './types/reservation-detail.types'
import { useConfirm } from 'hic-components'
import { useHourlyResourceTypeCheck } from 'src/app/hooks/hourly-resource-type/hourly-resource-type'

type ReservationDetailDataConsumerProps = {
  propertyId: string
  defaultReservedResourceId?: string
  refetchReservation: () => void
  setLoading: (value: boolean) => void
  reservation: ReservationF
  bills: BillF[]
  deposits: DepositF[]
  reservationLink: string | null
  onCancel: () => void
  readOnly: boolean
  defaultTabIndex?: ReservationHeaderTab
  unsavedChanges: (rrId: string | undefined) => void
}

export const ReservationDetailViewer = (props: ReservationDetailDataConsumerProps) => {
  const { propertyId, defaultReservedResourceId, refetchReservation, setLoading, reservation, bills, deposits, reservationLink, onCancel, readOnly, defaultTabIndex, unsavedChanges } = props
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const { sm } = useBreakpoints()

  const [activeTab, setActiveTab] = useState(defaultTabIndex ?? ReservationHeaderTab.RESERVED_RESOURCES)
  const [reservedResourceId, setReservedResourceId] = useState<string | null>(null)

  function onMailClick() {
    document.getElementById('reservation-tabs-list')?.scrollIntoView({ behavior: 'smooth' })
    setTimeout(() => {
      // using a timeout to avoid the rerender of the components
      setActiveTab(ReservationHeaderTab.MESSAGES)
    }, 500)
  }

  const showErrorSnackBar = useErrorSnackBar()
  const confirm = useConfirm()
  const [deleteReservationMutation] = useReservationDeleteMutation()
  const onDelete = useCallback(() => {
    confirm({
      title: t('pms.booking.reservation.header_deletion_title'),
      message: t('pms.booking.reservation.header_deletion_message'),
      confirmButtonText: t('commons.delete'),
      confirmIsDanger: true,
      cancelButtonText: t('commons.cancel'),
      onConfirm: () => {
        setLoading(true)
        deleteReservationMutation({
          variables: {
            tenantId,
            id: props.reservation.id,
          },
          refetchQueries: ['ReservationSearch'],
        })
          .then(() => {
            onCancel()
          })
          .catch(showErrorSnackBar)
          .finally(() => {
            setLoading(false)
          })
      },
    })
  }, [tenantId, reservation.id, setLoading])

  const [messagesHaveProblems, setMessagesHasProblems] = useState(false)

  const { data: reservationEmailsData, loading: loadingReservationEmails } = useGetReservationEmailsQuery({
    variables: {
      tenantId: tenantId,
      reservationId: reservation.id,
    },
  })

  const { data: reservationThreadData, loading: reservationThreadLoading } = useReservationThreadsQuery({
    variables: {
      filter: {
        propertyId: propertyId,
        tenantId: tenantId,
        reservationId: reservation.id,
      },
    },
  })

  const reservationEmails = useMemo(() => {
    const tmpReservationEmails = reservationEmailsData?.getReservationEmails ?? []
    const problems =
      tmpReservationEmails.find((email) => {
        return email.emailState === 'BOUNCE' || email.emailState === 'ERROR'
      }) !== undefined
    setMessagesHasProblems(problems)
    return tmpReservationEmails
  }, [reservationEmailsData?.getReservationEmails, setMessagesHasProblems])

  const reservationThread: ReservationThread[] = useMemo(() => {
    return (reservationThreadData?.threads ?? []).map((thread) => ({
      id: thread.id,
      lastMessageDate: thread.lastMessage?.createdOn ?? null,
      lastMessage: thread.lastMessage?.message ?? '',
      guestName: reservation.reference?.mainGuest?.name ?? '',
      archived: thread.archived,
    }))
  }, [reservationThreadData?.threads, reservation.reference?.mainGuest?.name])

  const hourlyResourceTypeExists = useHourlyResourceTypeCheck(tenantId, propertyId)

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <ReservationHeader readOnly={readOnly} reservation={reservation} deposits={deposits} bills={bills} onDelete={onDelete} onMailClick={onMailClick} reservationLink={reservationLink} />
        </Grid>
        {!sm && reservation?.reservedResources && reservation.reservedResources.length > 1 && (
          <Grid item xs={12}>
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }} spacing={1}>
              <HiCIcon>
                <FontAwesomeIcon icon={faFilter} style={{ fontSize: '1.25rem' }} />
              </HiCIcon>
              <Box flexGrow={2}>
                <ReservedResourceFilter reservation={reservation} onSelectedReservedResourceChange={setReservedResourceId} defaultReservedResourceId={defaultReservedResourceId} />
              </Box>
            </Stack>
          </Grid>
        )}

        <Grid item xs={12} id="reservation-tabs-list">
          <Stack direction="row" sx={{ alignItems: 'center', width: '100%' }}>
            <Tabs
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              showFilterIcon={reservedResourceId !== null}
              showMessagesWarningIcon={messagesHaveProblems}
              hourlyResourceTypeExists={hourlyResourceTypeExists}
            />

            {sm && reservation?.reservedResources && reservation.reservedResources.length > 1 && (
              <Stack direction="row" sx={{ marginLeft: 'auto', alignItems: 'center' }} spacing={1}>
                <HiCIcon display="block">
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: '1.25rem' }} />
                </HiCIcon>
                <Box>
                  <ReservedResourceFilter reservation={reservation} onSelectedReservedResourceChange={setReservedResourceId} defaultReservedResourceId={defaultReservedResourceId} />
                </Box>
              </Stack>
            )}
          </Stack>
          <Divider light />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ width: '100%' }}>
            <TabPanel panelIndex={ReservationHeaderTab.RESERVED_RESOURCES} activeTab={activeTab}>
              <ReservedResourcesTab //
                readOnly={readOnly}
                checkinWasDone={reservedResourceId !== null && reservation.reservedResources?.find(({ id }) => id === reservedResourceId)?.phase === ReservedResourcePhase.CHECKIN}
                propertyId={propertyId}
                bills={bills}
                deposits={deposits}
                reservedResourceId={reservedResourceId}
                setLoading={setLoading}
                refetchReservation={refetchReservation}
                unsavedChanges={unsavedChanges}
              />
            </TabPanel>
            <TabPanel panelIndex={ReservationHeaderTab.RESERVED_RESOURCES_HOURLY} activeTab={activeTab}>
              <TimeReservedResourcesTab //
                readOnly={readOnly}
                propertyId={propertyId}
                bills={bills}
                deposits={deposits}
                reservedResourceId={reservedResourceId}
                setLoading={setLoading}
                refetchReservation={refetchReservation}
                unsavedChanges={unsavedChanges}
              />
            </TabPanel>
            <TabPanel panelIndex={ReservationHeaderTab.BILLS} activeTab={activeTab}>
              <BillsTab //
                propertyId={propertyId}
                reservation={reservation}
                reservedResourceId={reservedResourceId}
                bills={bills}
                deposits={deposits}
                readOnly={readOnly}
              />
            </TabPanel>
            <TabPanel panelIndex={ReservationHeaderTab.MESSAGES} activeTab={activeTab}>
              <MessagesTab
                reservationEmails={reservationEmails}
                reservationThread={reservationThread}
                loading={loadingReservationEmails || reservationThreadLoading}
                propertyId={propertyId}
                tenantId={tenantId}
                reservationId={reservation.id}
                guestEmail={reservation.reference?.mainGuest?.mailContacts?.find((mail) => mail.type === 'MAIN')?.value ?? undefined}
                disableAutomation={reservation.disableAutomation ?? false}
              />
            </TabPanel>
            <TabPanel panelIndex={ReservationHeaderTab.ATTACHES} activeTab={activeTab}>
              <AttachesTab files={reservation.files ?? []} propertyId={propertyId} tenantId={tenantId} reservationId={reservation.id} />
            </TabPanel>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

type ReservationDetailDataCourierProps = {
  propertyId: string
  defaultReservedResourceId?: string
  refetchReservation: () => void
  setLoading: (value: boolean) => void
  bills: BillF[]
  deposits: DepositF[]
  reservationLink: string | null
  onCancel: () => void
  readOnly: boolean
  defaultTabIndex?: number
  unsavedChanges: (rrWithUnsavedChanges?: string) => void
}

export const ReservationDetailDataCourier = (props: ReservationDetailDataCourierProps) => {
  const { reservation, reservedResources } = useContext(ReservationContext)

  useEffect(() => {
    let itemFound: string | undefined = undefined
    reservedResources.forEach((value, key) => {
      if (!itemFound && value.hasChanged) itemFound = key
    })
    props.unsavedChanges(itemFound)
  }, [JSON.stringify(reservedResources)])

  if (reservation && reservation.customerReferenceList) return <ReservationDetailViewer {...props} reservation={reservation} />
  else return <></>
}
